/**
 * Example Filters Component
 *
 * Provides faceted filtering for the example library.
 */

import { useState, useCallback } from 'react';
import type {
  ExampleCategory,
  ComplexityLevel,
  HVACSystemType,
  ExampleFilterFacets,
} from '../types';
import { EXAMPLE_CATEGORIES, COMPLEXITY_LEVELS, HVAC_SYSTEM_TYPES } from '../types';

export interface ExampleFiltersProps {
  facets: ExampleFilterFacets;
  selectedCategories: ExampleCategory[];
  selectedComplexity: ComplexityLevel[];
  selectedHVACSystems: HVACSystemType[];
  onCategoriesChange: (categories: ExampleCategory[]) => void;
  onComplexityChange: (levels: ComplexityLevel[]) => void;
  onHVACSystemsChange: (systems: HVACSystemType[]) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  pendingMode?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
}

export function ExampleFilters({
  facets,
  selectedCategories,
  selectedComplexity,
  selectedHVACSystems,
  onCategoriesChange,
  onComplexityChange,
  onHVACSystemsChange,
  onClearFilters,
  hasActiveFilters,
  pendingMode = false,
  onApply,
  onCancel,
}: ExampleFiltersProps) {
  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['category', 'complexity'])
  );

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const toggleCategory = useCallback(
    (category: ExampleCategory) => {
      if (selectedCategories.includes(category)) {
        onCategoriesChange(selectedCategories.filter((c) => c !== category));
      } else {
        onCategoriesChange([...selectedCategories, category]);
      }
    },
    [selectedCategories, onCategoriesChange]
  );

  const toggleComplexity = useCallback(
    (level: ComplexityLevel) => {
      if (selectedComplexity.includes(level)) {
        onComplexityChange(selectedComplexity.filter((l) => l !== level));
      } else {
        onComplexityChange([...selectedComplexity, level]);
      }
    },
    [selectedComplexity, onComplexityChange]
  );

  const toggleHVACSystem = useCallback(
    (system: HVACSystemType) => {
      if (selectedHVACSystems.includes(system)) {
        onHVACSystemsChange(selectedHVACSystems.filter((s) => s !== system));
      } else {
        onHVACSystemsChange([...selectedHVACSystems, system]);
      }
    },
    [selectedHVACSystems, onHVACSystemsChange]
  );

  const complexityColors: Record<ComplexityLevel, string> = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-orange-500',
    expert: 'bg-red-500',
  };

  return (
    <div className="space-y-4">
      {/* Category Section */}
      <FilterSection
        title="Category"
        isExpanded={expandedSections.has('category')}
        onToggle={() => toggleSection('category')}
        selectedCount={selectedCategories.length}
      >
        <div className="space-y-1">
          {facets.categories.map(({ category, count }) => (
            <label
              key={category}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={() => toggleCategory(category)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="flex-1 text-sm">{EXAMPLE_CATEGORIES[category]?.name ?? category}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Complexity Section */}
      <FilterSection
        title="Difficulty"
        isExpanded={expandedSections.has('complexity')}
        onToggle={() => toggleSection('complexity')}
        selectedCount={selectedComplexity.length}
      >
        <div className="space-y-1">
          {facets.complexityLevels.map(({ complexity, count }) => (
            <label
              key={complexity}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={selectedComplexity.includes(complexity)}
                onChange={() => toggleComplexity(complexity)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className={`h-2 w-2 rounded-full ${complexityColors[complexity]}`} />
              <span className="flex-1 text-sm">{COMPLEXITY_LEVELS[complexity]?.name ?? complexity}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* HVAC Systems Section */}
      <FilterSection
        title="HVAC System"
        isExpanded={expandedSections.has('hvac')}
        onToggle={() => toggleSection('hvac')}
        selectedCount={selectedHVACSystems.length}
      >
        <div className="max-h-48 space-y-1 overflow-y-auto">
          {facets.hvacSystems.map(({ hvacSystem, count }) => (
            <label
              key={hvacSystem}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={selectedHVACSystems.includes(hvacSystem)}
                onChange={() => toggleHVACSystem(hvacSystem)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="flex-1 text-sm">{HVAC_SYSTEM_TYPES[hvacSystem]?.name ?? hvacSystem}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Learning Concepts Section */}
      <FilterSection
        title="Topics Covered"
        isExpanded={expandedSections.has('concepts')}
        onToggle={() => toggleSection('concepts')}
        selectedCount={0}
      >
        <div className="max-h-48 space-y-1 overflow-y-auto">
          {facets.concepts.slice(0, 10).map(({ concept, count }) => (
            <div
              key={concept}
              className="flex items-center justify-between px-2 py-1 text-sm"
            >
              <span className="capitalize">{concept.replace(/-/g, ' ')}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </div>
          ))}
        </div>
      </FilterSection>

      {/* Actions */}
      <div className="border-t pt-4">
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="mb-2 w-full rounded-md border px-3 py-2 text-sm hover:bg-muted"
          >
            Clear all filters
          </button>
        )}
        {pendingMode && (
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 rounded-md border px-3 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={onApply}
              className="flex-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface FilterSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  selectedCount: number;
  children: React.ReactNode;
}

function FilterSection({
  title,
  isExpanded,
  onToggle,
  selectedCount,
  children,
}: FilterSectionProps) {
  return (
    <div className="border-b pb-3">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-1 text-sm font-medium hover:text-primary"
      >
        <span className="flex items-center gap-2">
          {title}
          {selectedCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
              {selectedCount}
            </span>
          )}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {isExpanded && <div className="mt-2">{children}</div>}
    </div>
  );
}
